import type { CheckoutSimple, WovarCheckout } from "@commercekit/api";
import { formatMoneyWithCurrency } from "@commercekit/shared";
import type { FC } from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useHydrated } from "remix-utils/use-hydrated";
import { Country, useLocale } from "~/index";

export const CheckoutCtx = createContext<{
  checkout: WovarCheckout | null;
  tempCart: WovarCheckout | null;
  updateTempCart: (cart: WovarCheckout) => void;
  phoneCodeCountries: Country[];
  setPhoneCodeCountries: (countries: Country[]) => void;
  registerData: any;
}>({
  checkout: null,
  tempCart: null,
  updateTempCart: () => {},
  phoneCodeCountries: [],
  setPhoneCodeCountries: () => {},
  registerData: null,
});

export const CheckoutCtxProvider: FC<React.PropsWithChildren<{ checkout: WovarCheckout | null }>> = ({ children, checkout }) => {
  const isHydrated = useHydrated();
  const { localeFullName } = useLocale();
  const [tempCart, setTempCart] = useState<WovarCheckout | null>(null);
  const [phoneCodeCountries, setPhoneCodeCountries] = useState<Country[]>([]);
  const updateTempCart = useCallback(
    (cart: WovarCheckout | null) => {
      setTempCart(cart);
    },
    [checkout, tempCart]
  );
  const thresholdValues = useMemo(() => {
    if (isHydrated) {
      const smallOrderThreshold = checkout?.checkoutRules?.smallOrderThreshold ?? 0;
      const orderIsSmall = checkout?.subtotalExclTax?.value < smallOrderThreshold;

      const paymentMethodChargeValue = checkout?.customLineItems?.find((item) => item.sku === "WOV_PAYMENT_METHOD_CHARGE")?.price;
      const paymentMethodCharge = paymentMethodChargeValue
        ? formatMoneyWithCurrency(localeFullName, paymentMethodChargeValue)
        : null;
      const surchargeValue = checkout?.customLineItems?.find((item) => item.sku === "WOV_SMALL_ORDER_CHARGE")?.price;
      const surcharge = surchargeValue ? formatMoneyWithCurrency(localeFullName, surchargeValue) : null;

      const threshold = smallOrderThreshold
        ? formatMoneyWithCurrency(localeFullName, {
            value: smallOrderThreshold,
            currencyCode: checkout?.subtotalExclTax?.currencyCode,
          })
        : null;
      return {
        smallOrderThreshold,
        orderIsSmall,
        surcharge,
        surchargeValue: surchargeValue?.value,
        paymentMethodCharge,
        paymentMethodChargeValue: paymentMethodChargeValue?.value,
        threshold,
      };
    }
    return {};
  }, [checkout, isHydrated]);

  return (
    <CheckoutCtx.Provider
      value={{
        checkout: { ...checkout, ...thresholdValues },
        updateTempCart,
        tempCart,
        registerData: isHydrated ? JSON.parse(sessionStorage?.getItem("registerData") ?? "{}") : null,
        phoneCodeCountries,
        setPhoneCodeCountries,
      }}
    >
      {children}
    </CheckoutCtx.Provider>
  );
};

export const useCheckout = () => useContext(CheckoutCtx);

export const CheckoutSimpleCtx = createContext<{
  checkoutSimple: CheckoutSimple | null;
  tempCart: CheckoutSimple | null;
  updateTempCartSimple: (cart: CheckoutSimple) => void;
}>({
  checkoutSimple: null,
  tempCart: null,
  updateTempCartSimple: () => {},
});

export const CheckoutSimpleCtxProvider: FC<React.PropsWithChildren<{ checkoutSimple: CheckoutSimple | null }>> = ({
  children,
  checkoutSimple,
}) => {
  const [tempCart, setTempCart] = useState<CheckoutSimple | null>(null);
  const updateTempCartSimple = useCallback(
    (cart: CheckoutSimple | null) => {
      setTempCart(cart);
    },
    [checkoutSimple, tempCart]
  );
  return (
    <CheckoutSimpleCtx.Provider
      value={{
        checkoutSimple,
        updateTempCartSimple,
        tempCart,
      }}
    >
      {children}
    </CheckoutSimpleCtx.Provider>
  );
};
export const useCheckoutSimple = () => useContext(CheckoutSimpleCtx);

export * from "./useCheckout/useCheckoutAddress";
export * from "./useCheckout/useCheckoutContent";
export * from "./useCheckout/useCheckoutPayment";
export * from "./useCheckout/useCheckoutSignUp";
export * from "./useCheckout/useCheckoutStep";
export * from "./useCheckout/useCheckoutCountry";
